import { Section, VStack } from 'tio-ui/components/layout';
import PageHero from 'tio-ui/components/page-hero';
import Card from 'tio-ui/components/card';
import Heading from 'tio-ui/components/heading';
import { or } from 'tio-ui/utilities';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import Component from '@glimmer/component';
import formatPhone from 'tio-common/helpers/format-phone';
import Svg from 'tio-employee/components/svg';
import type { CompanySettingExtension as CompanySettingModel } from 'tio-data/data/resources/company-setting.ext';
import type PartnerService from 'tio-employee/services/partner';
import type ZendeskService from 'tio-employee/services/zendesk';

export interface S {
  Args: {
    model: CompanySettingModel;
  };
}

export default class ContactUs extends Component<S> {
  @service declare zendesk: ZendeskService;
  @service declare partner: PartnerService;

  get support() {
    return this.partner.supportModule;
  }

  get isZendeskEnabled() {
    return this.zendesk.isEnabledForPartner;
  }

  get supportEmail() {
    if (!this.support || this.support?.tioEmail.isEnabled) {
      return 'support@tuition.io';
    } else if (this.support?.partnerEmail) {
      return this.support.partnerEmail;
    } else {
      return false;
    }
  }

  get tioPhoneEnabled() {
    return !this.support || this.support?.tioPhone.isEnabled;
  }

  get partnerPhoneNumber() {
    if (this.support?.partnerPhone) {
      return this.support.partnerPhone;
    } else {
      return false;
    }
  }

  onClickChat = () => {
    this.zendesk.openWebChatWindow();
  };

  <template>
    <VStack class="gap-8">
      <PageHero
        @title={{t "contact_us.default"}}
        @gradient="brand-soft"
        class="tio-elevation-card-raised"
      />
      <Section>
        <:header>
          {{t "contact_us.ways_to_reach_us"}}
        </:header>
        <:body>
          <div class="app-card-grid app-card-grid--medium">
            {{#if this.supportEmail}}
              <Card
                @radius="card"
                @elevation="card"
                @padding="md"
                class="tio-stack-center text-center"
              >
                <Svg @name="email" width="45" height="45" />
                <Heading @level={{3}} @role="panel-title" class="mt-2">{{t
                    "contact_us.email_us"
                  }}</Heading>
                <a href="mailto:{{this.supportEmail}}" class="app-text-link tio-text-sm-medium">
                  {{this.supportEmail}}
                </a>
              </Card>
            {{/if}}
            {{#if (or this.partnerPhoneNumber this.tioPhoneEnabled)}}
              <Card
                @radius="card"
                @elevation="card"
                @padding="md"
                class="tio-stack-center text-center"
              >
                <Svg @name="call" width="45" height="45" />
                <Heading @level={{3}} @role="panel-title" class="mt-2">{{t
                    "contact_us.call_us"
                  }}</Heading>
                <div class="tio-text-sm-normal tio-fg-muted mt-1">
                  {{#if this.tioPhoneEnabled}}
                    <p>{{t "contact_us.call_us_times" htmlSafe=true}}</p>
                    <p>{{t "contact_us.tio_support_phone"}}</p>
                  {{else if this.partnerPhoneNumber}}
                    <p>{{formatPhone this.partnerPhoneNumber}}</p>
                  {{/if}}
                </div>
              </Card>
            {{/if}}
            {{#if this.isZendeskEnabled}}
              <Card
                @radius="card"
                @elevation="card"
                @padding="md"
                class="tio-stack-center text-center"
              >
                <Svg @name="headset_mic" width="45" height="45" />
                <Heading @level={{3}} @role="panel-title" class="mt-2">{{t
                    "contact_us.chat_with_us"
                  }}</Heading>
                <button
                  type="button"
                  class="app-text-link tio-text-sm-medium"
                  {{on "click" this.onClickChat}}
                >{{t "contact_us.live_chat_now"}}</button>
              </Card>
            {{/if}}
          </div>
        </:body>
      </Section>
    </VStack>
  </template>
}
